import { router } from '@/router';
import { ActionIcon, AppShell, Burger, Group, NavLink, ScrollArea, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import cx, { ClassValue } from 'clsx';
import useIsAuthenticated from 'react-auth-kit/hooks/useIsAuthenticated';
import useAuthUser from 'react-auth-kit/hooks/useAuthUser';
import useSignOut from 'react-auth-kit/hooks/useSignOut';
import { Navigate, Outlet, NavLink as RouterLink, useNavigate } from 'react-router-dom';
import { Role } from '@/__generated__/graphql';
import { useMutation } from '@apollo/client';
import { signOutDocument } from '@/features/auth/graphql/mutations';
import { notifications } from '@mantine/notifications';
import { CommentList } from '@/comments';
import { IconLogout } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';

function flattenRoutes(routes: typeof router.routes, basePath = ''): typeof router.routes {
  return routes.flatMap((route) => {
    const fullPath = basePath + (route.path ?? '');
    const isValid = route.path && !route.path.includes(':') && fullPath !== '/login';

    // Include the current route if it's valid
    const currentRoute = isValid ? { ...route, fullPath } : null;

    // Process children recursively and add them to the result
    const childRoutes = route.children ? flattenRoutes(route.children, fullPath) : [];

    // Return the current route (if valid) and its children
    return currentRoute ? [currentRoute, ...childRoutes] : childRoutes;
  });
}

export function Layout() {
  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();
  const [mobileOpened, { toggle: toggleMobile }] = useDisclosure(false);
  const [desktopOpened, { toggle: toggleDesktop }] = useDisclosure(true);

  const [mobileAsideOpened, { toggle: toggleMobileAside }] = useDisclosure(false);
  const [desktopAsideOpened, { toggle: toggleDesktopAside }] = useDisclosure(false);

  const [now, setNow] = useState(dayjs());

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(dayjs());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const user = useAuthUser<{ id: string; email: string; role: Role }>();
  const signOutLocal = useSignOut();

  const [signOut, { loading }] = useMutation(signOutDocument, {
    awaitRefetchQueries: true,
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ signOut: { success } }) => {
      if (!success) return notifications.show({ message: 'Odhlásenie zlyhalo' });

      signOutLocal();
      navigate('/sign-in');

      notifications.show({
        message: 'Úspešne ste sa odhlásili',
        color: 'green',
      });
    },
  });

  if (!isAuthenticated || !user) return <Navigate to="/sign-in" />;

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{
        width: 300,
        breakpoint: 'md',
        collapsed: { mobile: !mobileOpened, desktop: !desktopOpened },
      }}
      aside={{
        width: 400,
        breakpoint: 'md',
        collapsed: { mobile: !mobileAsideOpened, desktop: !desktopAsideOpened },
      }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group justify="space-between" align="center" gap="xs">
            <Group>
              <Burger opened={mobileOpened} onClick={toggleMobile} hiddenFrom="md" size="sm" />
              <Burger opened={desktopOpened} onClick={toggleDesktop} visibleFrom="md" size="sm" />
            </Group>
            <Group>
              <Text c="dimmed">{user.email}</Text>
              <ActionIcon
                variant="subtle"
                onClick={async () => {
                  await signOut();
                }}
                loading={loading}
              >
                <IconLogout size={16} />
              </ActionIcon>
            </Group>
          </Group>
          <Text c="dimmed" ff="monospace" visibleFrom="md">
            {now.format('HH:mm:ss')}
          </Text>
          <Group>
            <Burger
              opened={mobileAsideOpened}
              onClick={toggleMobileAside}
              hiddenFrom="md"
              size="sm"
            />
            <Burger
              opened={desktopAsideOpened}
              onClick={toggleDesktopAside}
              visibleFrom="md"
              size="sm"
            />
          </Group>
        </Group>
      </AppShell.Header>
      <AppShell.Navbar p="md">
        <AppShell.Section grow component={ScrollArea}>
          {flattenRoutes(router.routes).map(({ path }) => (
            <NavLink
              key={path}
              label={path}
              renderRoot={({ className, ...others }) => (
                <RouterLink
                  className={({ isActive }) =>
                    cx(className as ClassValue, { 'active-class': isActive })
                  }
                  to={path}
                  {...others}
                />
              )}
            />
          ))}
        </AppShell.Section>
      </AppShell.Navbar>
      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
      <AppShell.Aside py="sm" px="xs">
        <CommentList opened={desktopAsideOpened || mobileAsideOpened} />
      </AppShell.Aside>
    </AppShell>
  );
}
